import React, {useCallback, useEffect, useMemo} from 'react';
import {classes, style, vars} from './doppe-viewer-action-view.st.css';
import type {DoppeActionComponentActionButtonProps, DoppeActionView} from '../../doppe-sdk';
import {useBymoPageContextProps} from '../../doppe-sdk';
import {
    arrayImmutableRemove,
    colorIsDark,
    evaluateFunction,
    uiPartDefineGroups,
    uiPartsGroupCreateMarking, HideableValue } from '@wix/devzai-utils-common';
import type {DoppeDtoAction} from '../../client-server-common/types/doppe-dto-action';
import {
    DoppeDtoActionViewType,
    doppeDtoActionViewTypeIsDisplayedAsButton
} from '../../client-server-common/types/doppe-dto-action';
import {
    CollapsibleCard,
    reactCreateMemo,
    useDynamicRef,
    useObservable,
    useRootWindow,
    useWasVisibleInViewport
} from '@wix/devzai-utils-react';
import {
    bymoPageStyleGetStyleScopeClassName,
    BymoPageStyleScopeColors,
    BymoPageStyleScopeContext,
    bymoPageStyleVars,
    useBymoPageScopeColor
} from '../bymo-page-style/bymo-page-style';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {
    DoppePageMediaViewLoading,
    DoppePageMediaViewPreferencesScope
} from '../doppe-page-media-view/doppe-page-media-view';
import {BymoPageStyleScope, DoppeActionWidgetOpeningMode} from '../../client-server-common';
import {DomEventListener} from '@wix/devzai-utils-dom';
import {BymoPageViewerMode, useBymoPageViewerContext} from '../bymo-page-viewer-context/bymo-page-viewer-context';
import {DoppeActionGoalType} from '../../client-server-common/doppe-action-types/doppe-action-type';

export const DoppeViewerActionViewUiPartsGroups = uiPartDefineGroups({
    ActionFrameElement: 'doppe-viewer-action-view-action-frame'
});

const tappedStateClass = arrayImmutableRemove(style(classes.root, {actionButtonTapped: true}).split(' '), classes.root).join(' ');

export const DoppeViewerActionView = reactCreateMemo(function DoppeViewerActionView(props: DoppeViewerActionView.Props) {
    const {
        label,
        actionImage,
        bannerMedia,
        actionDescription,
        viewType,
        renderActionWidget,
        activateCTA,
        isWidgetOpener,
        actionContext,
        skipReportingOnGoalAchievedOnActionButtonClick,
        actionButtonComponentProps,
        isExpanded,

        actionViewRootHtmlAttributes,

        actionWidgetContainerClassName,
        actionButtonContainerClassName,
        actionWidgetFramelessContainerClassName,
        actionWidgetRoundness,
        renderActionButton,
        renderInlineContentCloseButton,
        isLockedObservable,

        forcedBymoPageStyleScope,

        className,
        style: styleProp,
    } = props;

    const rootWindow = useRootWindow();

    const bymoPageContextProps = useBymoPageContextProps();
    const pageViewerContext = useBymoPageViewerContext();
    const textColor = useBymoPageScopeColor(BymoPageStyleScopeColors.TextColor, BymoPageStyleScope.PageContent);
    const isDarkForeground = useMemo(() => colorIsDark(textColor), [textColor]);

    const onBeforeTogglingAnimationStart = useCallback<Required<CollapsibleCard.Props>['onBeforeTogglingAnimationStart']>((element) => {
        element.style.overflow = 'hidden';
    }, []);

    const onTogglingAnimationEnd = useCallback<Required<CollapsibleCard.Props>['onTogglingAnimationEnd']>((element) => {
        element.style.overflow = '';
    }, []);

    const actionButtonLabel = evaluateFunction(() => {
        if (viewType === DoppeDtoActionViewType.BannerButton) {
            return label;
        } else {
            return undefined;
        }
    });

    useObservable(isLockedObservable);

    const rootRef = useDynamicRef<HTMLDivElement>();
    const wasVisible = useWasVisibleInViewport(rootRef);

    useEffect(() => {
        if (wasVisible) {
            actionContext.reportActionImpression();
        }
    }, [wasVisible]);

    const bymoPageStyleScope = forcedBymoPageStyleScope ?
        forcedBymoPageStyleScope :
        (viewType === DoppeDtoActionViewType.InPageFrameless ? BymoPageStyleScope.PageContent : BymoPageStyleScope.Card)

    return (
        <section
            {...actionViewRootHtmlAttributes}
            style={{
                ...styleProp,
                ...actionViewRootHtmlAttributes.style,
                // [vars.contextMenuTargetColor]: isDarkForeground ? 'rgba(0,0,0,0.8)' : 'rgba(255,255,255,0.8)',
                [vars.contextMenuTargetColor]: isDarkForeground ? '#0773bb' : '#AADBFC',
                [vars.selectedInEditorColor]: isDarkForeground ? '#0773bb' : '#AADBFC',
            }}
            className={style(
                classes.root,
                {
                    isSelectedInEditor: pageViewerContext.mode === BymoPageViewerMode.LivePreview && (
                        bymoPageContextProps.editorSelectedActionsIds?.includes(actionContext.action.id) ?? false
                    ),
                    disableInPageInteractivity: bymoPageContextProps.editorDisableInPageInteractivity ?? false,
                    viewType: evaluateFunction(() => {
                        switch (viewType) {
                            case DoppeDtoActionViewType.InPageFrameless:
                                return 'strip';
                            case DoppeDtoActionViewType.ActionButton:
                                return 'button';
                            case DoppeDtoActionViewType.InPage:
                                return 'card';
                            case DoppeDtoActionViewType.BannerButton:
                                return 'banner';
                        }
                    })
                },
                className,
                actionViewRootHtmlAttributes.className
            )}
            // title={actionButtonLabel}
            aria-label={actionButtonLabel}
            ref={rootRef}
        >
            {evaluateFunction(() => {
                if (doppeDtoActionViewTypeIsDisplayedAsButton(viewType)) {

                    return (
                        <CollapsibleCard
                            transitionDuration={300}
                            isOpen={isExpanded}
                            onBeforeTogglingAnimationStart={onBeforeTogglingAnimationStart}
                            onTogglingAnimationEnd={onTogglingAnimationEnd}
                            style={{
                                [bymoPageStyleVars.actionWidgetRoundness]: `${actionWidgetRoundness}px`
                            }}
                            header={() => {
                                const {
                                    onClick,
                                    ...htmlAttributes
                                } = actionButtonComponentProps;

                                return (
                                    <div
                                        className={style(classes.actionButtonContainer, actionButtonContainerClassName)}
                                        {...uiPartsGroupCreateMarking(DoppeViewerActionViewUiPartsGroups.ActionFrameElement)}
                                        onTouchStart={() => {
                                            const rootElement = rootRef.current;

                                            if (rootElement) {
                                                rootElement.classList.add(tappedStateClass);

                                                DomEventListener.one((rootWindow ?? window).document, 'touchend', () => {
                                                    rootElement.classList.remove(tappedStateClass);
                                                })
                                            }
                                        }}
                                    >
                                        {renderActionButton({
                                            actionButtonComponentProps: {
                                                onClick: (event) => {

                                                    const originalCallback = () => {
                                                        if (isWidgetOpener) {
                                                            if (actionContext.canBeToggled()) {
                                                                actionContext.toggleActionView(true);
                                                            }
                                                        } else if (actionContext.action.widgetOpeningMode === DoppeActionWidgetOpeningMode.OpenCTA) {
                                                            activateCTA?.();
                                                        }

                                                        if (!skipReportingOnGoalAchievedOnActionButtonClick) {
                                                            actionContext.reportGoalAchieved({
                                                                goalType: DoppeActionGoalType.Click
                                                            });
                                                        }

                                                        onClick?.(event)
                                                    }

                                                    if (!(isWidgetOpener && actionContext.canBeToggled()) && isLockedObservable && isLockedObservable.getValue()) {
                                                        void evaluateFunction(async () =>
                                                            actionContext.startUnlocking(() => {
                                                                isLockedObservable.setValue(false);
                                                            }, true)
                                                        )
                                                    } else {
                                                        originalCallback();
                                                    }
                                                },
                                                ...htmlAttributes
                                            },
                                            actionTitle: label,
                                            actionImage: actionImage,
                                            bannerMedia: bannerMedia,
                                            actionDescription: actionDescription,
                                            isWidgetOpener: isWidgetOpener,
                                            isLocked: isLockedObservable ? isLockedObservable.getValue() : false,
                                            actionViewType: viewType,
                                            actionId: actionContext.action.id
                                        })}
                                    </div>
                                );
                            }}
                            subContent={() => {
                                return renderInlineContentCloseButton({
                                    closingFunction: () => {
                                        actionContext.toggleActionView(false);
                                    }
                                })
                            }}
                        >
                            {/* These are media view preferences for the content of an action when the action is a collapsible card. */}
                            <DoppePageMediaViewPreferencesScope
                                forcePreciseWidthForDesktop={true}
                                forcePreciseWidthForMobile={true}
                                mediaLoading={DoppePageMediaViewLoading.LazyNative}
                            >
                                <BymoPageStyleScopeContext.Provider value={forcedBymoPageStyleScope ?? BymoPageStyleScope.Card}>
                                    {renderActionWidget({
                                        className: style(
                                            classes.actionWidgetContainer,
                                            actionWidgetContainerClassName,
                                            bymoPageStyleGetStyleScopeClassName(forcedBymoPageStyleScope ?? BymoPageStyleScope.Card)
                                        ),
                                        ...uiPartsGroupCreateMarking(DoppeViewerActionViewUiPartsGroups.ActionFrameElement)
                                    })}
                                </BymoPageStyleScopeContext.Provider>
                            </DoppePageMediaViewPreferencesScope>
                        </CollapsibleCard>
                    )
                } else {

                    // if (isLockedObservable?.getValue()) {
                    //     return <DoppeStandardLockWizard
                    //         actionContext={actionContext}
                    //         enableAnimation={false}
                    //         lockedAction={actionContext.action}
                    //         unlockedCallback={() => {
                    //             isLockedObservable.setValue(false);
                    //         }}
                    //         templateProps={{
                    //             roundness: actionWidgetRoundness
                    //         }}
                    //     />
                    // }
                    // else {
                    return (
                        <BymoPageStyleScopeContext.Provider
                            value={bymoPageStyleScope}
                        >
                            {renderActionWidget({
                                className: style(
                                    classes.actionWidgetContainer,
                                    viewType === DoppeDtoActionViewType.InPage ? actionWidgetContainerClassName : actionWidgetFramelessContainerClassName,
                                    bymoPageStyleGetStyleScopeClassName(bymoPageStyleScope)
                                ),
                                style: {
                                    [bymoPageStyleVars.actionWidgetRoundness]: `${actionWidgetRoundness}px`
                                },
                                ...uiPartsGroupCreateMarking(DoppeViewerActionViewUiPartsGroups.ActionFrameElement)
                            })}
                        </BymoPageStyleScopeContext.Provider>
                    )
                    // }
                }
            })}
        </section>
    )
});

export namespace DoppeViewerActionView {

    export interface ActionButtonRenderProps {
        actionButtonComponentProps: DoppeActionComponentActionButtonProps;
        isWidgetOpener: boolean;
        isLocked: boolean;
        actionTitle: string;
        actionDescription: HideableValue<string>;
        actionViewType: DoppeDtoActionViewType;
        bannerMedia: WixMediaResource | null;
        actionImage: HideableValue<WixMediaResource | null>;
        actionId: DoppeDtoAction.Id;
    }


    export interface Props extends DoppeActionView.ContextProps, Pick<React.HTMLAttributes<any>, 'className' | 'style'> {
        actionWidgetContainerClassName?: string;
        actionButtonContainerClassName?: string;
        actionWidgetRoundness: number;
        renderActionButton: (renderProps: ActionButtonRenderProps) => React.ReactElement;
        forcedBymoPageStyleScope?: BymoPageStyleScope;
    }
}
